//-----------------------------------------------------------------------
// Copyright © 2022 ManpowerGroup. All Rights Reserved.
//-----------------------------------------------------------------------

/* eslint-disable prettier/prettier */
import React, { useContext, useState, useEffect } from "react";
import { Col, Button, Container, Card, Row, Table, Form, Pagination, Tab, ListGroup } from "react-bootstrap";
import { useTable, useFilters, useGlobalFilter, usePagination } from "react-table";
import { useNavigate } from "react-router-dom";
import { Context } from "../../common/context/Context";
import { Link } from 'react-router-dom';
import { ManageClientsActions } from "../manageClients/manageClientsActions";
import ClientSpecificMapping from "../../clientSpecificTabs/clientSpecificMapping";
import ErrorAlertMessage from "../../common/alertMessages/errorAlertMessage";
import { getPageContentLabel } from "../../common/helpers/cmsLabelHelper";
import { getClientsPageCMSContent } from "./manageClientsCmsHelper";

function SelectColumnFilter({
    column: { filterValue, setFilter, preFilteredRows, id },
}) {
    const options = React.useMemo(() => {
        const options = new Set();
        preFilteredRows.forEach((row) => {
            options.add(row.values[id]);
        });
        return [...options.values()];
    }, [id, preFilteredRows]);

    return (
        <Form style={{ display: "flex" }}>
            <Form.Select
                id={id}
                aria-label="select filter"
                value={filterValue}
                onChange={(e) => {
                    (e.target.value != "All") ? setFilter(e.target.value || undefined) : setFilter(undefined);
                }}
            >
                <option value="All">All Countries</option>
                {options.map((option, i) => (
                    <option key={i} value={option}>
                        {option}
                    </option>
                ))}
            </Form.Select>
        </Form>
    );
}

function DefaultColumnFilter({
    column: { filterValue, preFilteredRows, setFilter, id },
}) {
    const count = preFilteredRows.length;

    const clearFilter = () => {
        setFilter(undefined);
    }

    return (
        <Form style={{ display: "flex" }}>
            <Form.Group>
                <Form.Control
                    id={id}
                    value={filterValue || ""}
                    onChange={(e) => {
                        setFilter(e.target.value || undefined);
                    }}
                    placeholder={`Search ${count} records...`}
                    className="mt-2"
                />
            </Form.Group>
            <Button
                onClick={(e) => clearFilter(e)}
                variant="secondary"
                className="close btn-sm"
                style={{ height: "100%", alignSelf: "end", marginLeft: "1px", padding: "0 0.25rem" }}
            >
                X
            </Button>
        </Form>
    );
}

const ColumnFilteringTable = ({ columns, data, cmsData }) => {

    const fetchCommonComponentLabel = (key) =>
        getPageContentLabel(cmsData, "CommonComponent", key);

    const returnPageIndexText = (currentPage, TotalPage) => {
        const pageText = fetchCommonComponentLabel("Common.Page");
        let replaceText = pageText.replace('Page', '<span>Page</span>').replace('#Currentpage#', '<strong>' + currentPage + '</strong>').replace('#Totalpage#', '<strong>' + TotalPage + '</strong>');
        return (
            <div className="me-2 mt-1" dangerouslySetInnerHTML={{ __html: replaceText }} />
        )
    }


    const filterTypes = React.useMemo(
        () => ({
            // Or, override the default text filter to use
            // "startWith"
            text: (rows, id, filterValue) => {
                return rows.filter((row) => {
                    const rowValue = row.values[id];
                    return rowValue !== undefined
                        ? String(rowValue)
                            .toLowerCase()
                            .startsWith(String(filterValue).toLowerCase())
                        : true;
                });
            },
        }),
        []
    );

    const defaultColumn = React.useMemo(
        () => ({
            // Let's set up our default Filter UI
            Filter: DefaultColumnFilter,
        }),
        []
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize },
    } = useTable(
        {
            columns,
            data,
            defaultColumn, // Be sure to pass the defaultColumn option
            filterTypes,
            initialState: { pageIndex: 0 },
        },
        useFilters, // useFilters!
        useGlobalFilter,// useGlobalFilter!
        usePagination
    );

    return (
        <Card>
            <Card.Body>
                <Table striped bordered {...getTableProps()} responsive>
                    <thead>
                        {headerGroups.map((headerGroup) => (
                            <tr {...headerGroup.getHeaderGroupProps()}>
                                {headerGroup.headers.map((column) => (
                                    <th className="m-4" {...column.getHeaderProps()}>
                                        {column.render("Header")}
                                        {/* Render the columns filter UI */}
                                        <div>
                                            {column.canFilter ? column.render("Filter") : null}
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody {...getTableBodyProps()}>
                        {page.map((row, i) => {
                            prepareRow(row);
                            return (
                                <tr {...row.getRowProps()}>
                                    {row.cells.map((cell) => {
                                        return (
                                            <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                                        );
                                    })}
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>
                <Row className="mt-3">
                    <Col md="6" className="d-flex flex-lg-row flex-column">
                        {returnPageIndexText(pageIndex + 1, pageOptions.length)}
                        <div className="me-2">
                            <span className="me-2"> {fetchCommonComponentLabel('Common.Show')}</span>
                            <Form.Select
                                className="d-inline-block w-auto"
                                value={pageSize}
                                aria-label="pageNumber"
                                onChange={(e) => {
                                    setPageSize(Number(e.target.value));
                                }}
                            >
                                {[10, 20, 30, 40, 50].map((pageSize) => (
                                    <option key={pageSize} value={pageSize}>
                                        {pageSize}
                                    </option>
                                ))}
                            </Form.Select>
                        </div>
                        <div className="me-2">
                            <span className="me-2"> {fetchCommonComponentLabel('Common.GoToPage')}</span>
                            <Form.Control
                                className="d-inline-block"
                                type="number"
                                aria-label="number"
                                min="1"
                                defaultValue={pageIndex + 1}
                                onChange={(e) => {
                                    const page = e.target.value ? Number(e.target.value) - 1 : 0;
                                    gotoPage(page);
                                }}
                                style={{ width: "75px" }}
                            />
                        </div>
                    </Col>
                    <Col md="6" className="mt-1">
                        <Pagination className="float-lg-end float-md-end float-left">
                            <Pagination.First
                                onClick={() => gotoPage(0)}
                                disabled={!canPreviousPage}
                            />
                            <Pagination.Prev
                                onClick={() => previousPage()}
                                disabled={!canPreviousPage}
                            />
                            <Pagination.Next
                                onClick={() => nextPage()}
                                disabled={!canNextPage}
                            />
                            <Pagination.Last
                                onClick={() => gotoPage(pageCount - 1)}
                                disabled={!canNextPage}
                            />
                        </Pagination>
                    </Col>
                </Row>
            </Card.Body>
        </Card>
    );
};

const ManageClient = () => {

    const context = useContext(Context);
    const [adminClientCMSData, setAdminClientCMSData] = useState({ title: "", description: "", tabNavigationTitle: "", pageContent: {} })
    const [manageClientsData, setManageClientsData] = useState({});
    const [showClients, setShowClients] = useState(false);
    const [userAccountData, setUserAccountData] = useState();
    const [errorAlert, setErrorAlert] = useState({
        showAlert: false
    })
    const showErrorModal = () => {
        let refErrorMessageAlert = JSON.parse(JSON.stringify(errorAlert));
        refErrorMessageAlert.showAlert = false;
        setErrorAlert(refErrorMessageAlert)
    }

    const fetchManageClientsData = async () => {
        let refErrorMessageAlert = JSON.parse(JSON.stringify(errorAlert));
        await ManageClientsActions.GetManageClientsList().then(
            (res) => {
                if (res.status === 200 || res.status === 204) {
                    let resData = res.data && res.data;
                    setManageClientsData(resData);
                    context.setManageClientsTableData(resData);
                    setShowClients(true);
                }
                else {
                    refErrorMessageAlert.showAlert = true;
                    setErrorAlert(refErrorMessageAlert);
                    window.scrollTo(0, 0);
                }
            },
            (err) => {
                console.log(err);
            }
        );
    }

    const fetchAdminClientCMSData = async () => {
        let refErrorMessageAlert = JSON.parse(JSON.stringify(errorAlert));
        const cmsContent = await getClientsPageCMSContent();
        if (cmsContent) {
            setAdminClientCMSData(cmsContent);
        }
        else {
            refErrorMessageAlert.showAlert = true;
            setErrorAlert(refErrorMessageAlert);
            window.scrollTo(0, 0);
        }
    }

    const fetchManageClientsLabel = (key) =>
        getPageContentLabel(adminClientCMSData, "ManageClientsComponent", key);

    useEffect(() => {
        fetchAdminClientCMSData();
        fetchManageClientsData();
    }, []);

    const trackAction = async (e, candidateId, candidateName, personaId, countryCode) => {

        let refErrorMessageAlert = JSON.parse(JSON.stringify(errorAlert));
        let todayDate = new Date();
        let userId = context.userAccountData && context.userAccountData?.id;

        let payload = {
            "StaffId": userId,
            "SourceId": candidateId + "," + countryCode + "," + personaId,
            "Type": "Clients",
            "Value": candidateName,
            "TriggeredTimeStamp": todayDate,
            "CreatedTimeStamp": todayDate,
            "CreatedBy": userId,
            "UpdatedTimeStamp": todayDate,
            "UpdatedBy": "NULL"
        }

        await ManageClientsActions.TrackAction(payload).then(
            (res) => {
                if (res.status === 200 || res.status === 204) {
                    console.log("api succeeded")
                }
                else {
                    refErrorMessageAlert.showAlert = true;
                    setErrorAlert(refErrorMessageAlert);
                    window.scrollTo(0, 0);
                }
            },
            (err) => {
                console.log(err);
            }
        );
    }

    const tableColumns = [
        {
            id: 'ManageClients.ClientCode',
            Header: fetchManageClientsLabel('ManageClients.ClientCode'),
            accessor: "companyId",
            Cell: ({ row, value }) =>
            (<Link to={'/clients-profile'}
                onClick={(e) => trackAction(e, value, row?.original?.companyName, row?.original?.personaId, row?.original?.programCountryCode)}
                state={{ clientId: value, clientName: row?.original?.companyName, personaId: row?.original?.personaId, countryCode: row?.original?.programCountryCode }}>
                {value}
            </Link>)
        },
        {
            id: "ManageClients.ClientName",
            Header: fetchManageClientsLabel('ManageClients.ClientName'),
            accessor: "companyName",
            Cell: ({ row, value }) =>
            (<Link to={'/clients-profile'}
                onClick={(e) => trackAction(e, row?.original?.companyId, row?.original?.companyName, row?.original?.personaId, row?.original?.programCountryCode)}
                state={{ clientId: row?.original?.companyId, clientName: row?.original?.companyName, personaId: row?.original?.personaId, countryCode: row?.original?.programCountryCode }}>
                {value}
            </Link>)
        },
        {
            id: "ManageClients.Country",
            Header: fetchManageClientsLabel('ManageClients.Country'),
            accessor: "programCountry",
            Filter: SelectColumnFilter,
        },
        {
            id: "ManageClients.Persona",
            Header: fetchManageClientsLabel('ManageClients.Persona'),
            accessor: "masterPersonaName",
        },
        {
            id: "ManageClients.NumberOfPrograms",
            Header: fetchManageClientsLabel('ManageClients.NumberOfPrograms'),
            accessor: "programClassCount",
            Filter: false,
        },
        {
            id: "ManageClients.NumberofActiveCandidates",
            Header: fetchManageClientsLabel('ManageClients.NumberofActiveCandidates'),
            accessor: "candidateCount",
            Filter: false,
        }
    ];

    return (
        <React.Fragment>
            <Col lg='12'>
                <Container fluid className="p-0">
                    {errorAlert?.showAlert &&
                        <ErrorAlertMessage alertMessage="AllPages.ErrorAlertMessage" showAlert={showErrorModal} />
                    }
                    <Row>
                        <Col md="6" lg="4" className="mt-3">
                            <div>
                                <h3> {adminClientCMSData?.title}</h3>
                            </div>
                        </Col>
                        <Col lg="12">
                            <Tab.Container id="clientspecific-tabs" defaultActiveKey="#">
                                <ListGroup horizontal className="mb-4">
                                    <ListGroup.Item action href="#">
                                        {adminClientCMSData?.pageContent["ManageClientsComponent"]?.Title}
                                    </ListGroup.Item>
                                    {context?.permissionsList?.includes('Client specific content.clientspecificcontent') &&
                                        <ListGroup.Item action href="#clientspecificmapping">
                                            {adminClientCMSData?.pageContent["ClientSpecificContentComponent"]?.Title}
                                        </ListGroup.Item>
                                    }
                                </ListGroup>
                                <Tab.Content>
                                    <Tab.Pane eventKey="#">
                                        <Container fluid className="p-0">
                                            {showClients ? manageClientsData && manageClientsData.length > 0 ?
                                                <ColumnFilteringTable
                                                    columns={tableColumns && tableColumns}
                                                    data={manageClientsData && manageClientsData}
                                                    cmsData={adminClientCMSData && adminClientCMSData}
                                                />
                                                : fetchManageClientsLabel('ManageClients.NoDataLabel')
                                                : ""
                                            }
                                        </Container>
                                    </Tab.Pane>
                                    {context?.permissionsList?.includes('Client specific content.clientspecificcontent') &&
                                        <Tab.Pane eventKey="#clientspecificmapping">
                                            <ClientSpecificMapping cmsData={adminClientCMSData && adminClientCMSData} />
                                        </Tab.Pane>
                                    }
                                </Tab.Content>
                            </Tab.Container>
                        </Col>
                    </Row>
                </Container>
            </Col>
        </React.Fragment>
    )
}




export default ManageClient;
